import type { ApexOptions } from 'apexcharts';

import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

interface IGameDistributionItem {
  label: string;
  value: number;
  color: string;
}

interface IGameDistributionChartProps {
  data: IGameDistributionItem[];
}

const GameDistributionChart: React.FC<IGameDistributionChartProps> = ({
  data,
}) => {
  const series = useMemo(() => data.map((item) => item.value), [data]);
  const labels = useMemo(() => data.map((item) => item.label), [data]);
  const colors = useMemo(() => data.map((item) => item.color), [data]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'donut',
        fontFamily: 'Aptos, sans-serif',
        toolbar: { show: false },
      },
      labels,
      colors,
      dataLabels: { enabled: false },
      legend: { show: false },
      stroke: { width: 0 },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: { show: false },
          },
          expandOnClick: false,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        custom: ({ seriesIndex }: { seriesIndex: number }) => {
          const item = data[seriesIndex];
          if (!item) return '';
          return `
            <div style="background:#ffffff;padding:6px 10px;font-family:Aptos,sans-serif;box-shadow:0 4px 12px rgba(24,27,37,0.08);width:max-content;max-width:240px;white-space:nowrap;">
              <div style="display:flex;align-items:center;gap:8px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${item?.color};flex-shrink:0;"></span>
                <span style="font-size:13px;color:#525866;">${item?.label}</span>
                <span style="font-size:12px;font-weight:500;color:#181B25;margin-left:auto;">${item?.value}%</span>
              </div>
            </div>`;
        },
      },
      states: {
        hover: { filter: { value: 0.85, type: 'lighten' } },
        active: { filter: { type: 'darken', value: 0.85 } },
      },
    }),
    [labels, colors, data]
  );

  return (
    <div className="flex items-center gap-5 min-w-0">
      {/* Donut */}
      <div className="shrink-0">
        <ReactApexChart
          options={options}
          series={series}
          type="donut"
          width={180}
          height={180}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-col gap-3 flex-1 min-w-0">
        {data?.map((item) => (
          <div
            key={item?.label}
            className="flex items-start justify-between gap-2"
          >
            <div className="flex items-start gap-2 min-w-0">
              <span
                className="w-2 h-2 rounded-sm shrink-0 mt-1.5"
                style={{ backgroundColor: item?.color }}
              />
              <span className="text-sm text-neutral-primary font-medium leading-5 wrap-break-word min-w-0">
                {item?.label}
              </span>
            </div>
            <span className="text-sm font-semibold text-neutral-primary shrink-0 leading-5">
              {item?.value}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GameDistributionChart;
